/**
 * Whose page, card and text colours win when a look and a brand disagree.
 *
 * The colour half of the rule fonts.ts states for typography. Reported (2026-10-19): a tenant set
 * all three Text colours to black, saved the brand, applied it to the site and published, and the
 * live listing cards did not change; the card background could not be changed either. The values
 * DID reach `site_style_settings` and the `:root` rule, and were then lost, because the chosen
 * look's `theme.surface` set the same four variables as an inline style on the page wrapper, and
 * an inline style beats any author-level rule. The Studio preview never applied the look's surface
 * at all, so it showed the tenant's colours while the published site showed the look's.
 *
 *   1. a colour the tenant AUTHORED for this site (its own style row) — always wins;
 *   2. the saved look's surface — fills only the roles nobody has chosen;
 *   3. the product default.
 *
 * Dark mode (the site-wide toggle or a dark look) is not this function's business: the callers
 * apply DARK_SURFACE_VARS after it, so a dark page stays dark, as it did before.
 *
 * `{ previewing: true }` is for AUDITIONING a look (`?previewLook=`), where the whole look is shown
 * as designed, same as lookFontVars.
 */

import { roleByKey } from './colorRoles';

/** A look's `theme.surface` (`HomeLookTheme` / `SectionLookTheme`). */
export interface LookSurfaceColors {
  background?: string | null;
  surface?: string | null;
  text?: string | null;
  textMuted?: string | null;
}

/**
 * Which of the four surface roles this site has a colour of its own for. Booleans keyed by
 * registry role; the values themselves already reach the page through `:root`.
 */
export interface AuthoredSurfaceColors {
  page_background?: boolean;
  surface?: boolean;
  text_primary?: boolean;
  text_secondary?: boolean;
}

/** A look's surface key → the registry role it lands on. The CSS var comes from the registry. */
export const LOOK_SURFACE_ROLES: ReadonlyArray<{ key: keyof LookSurfaceColors; role: keyof AuthoredSurfaceColors }> = [
  { key: 'background', role: 'page_background' },
  { key: 'surface', role: 'surface' },
  { key: 'text', role: 'text_primary' },
  { key: 'textMuted', role: 'text_secondary' },
];

export interface LookSurfaceOptions {
  /** The look is being auditioned, not worn: show its surface whatever the site says. */
  previewing?: boolean;
}

/** The colour custom properties a look may set on this site, given what the site authored. */
export function lookSurfaceVars(
  surface: LookSurfaceColors | null | undefined,
  authored: AuthoredSurfaceColors | null | undefined,
  opts: LookSurfaceOptions = {},
): Record<string, string> {
  const vars: Record<string, string> = {};
  if (!surface) return vars;
  for (const { key, role } of LOOK_SURFACE_ROLES) {
    const hex = surface[key]?.trim();
    const cssVar = roleByKey(role)?.cssVar;
    if (!hex || !cssVar) continue;
    if (!opts.previewing && authored?.[role]) continue;
    vars[cssVar] = hex;
  }
  return vars;
}
